import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  CHANNEL_STATUS,
  CHANNEL_STATUS_OBJECT,
  CHANNEL_TYPE,
  DEFAULT_ERROR,
  ERROR_MESSAGES,
  activeTranslations,
  PHONES_FOR_INPUT,
} from '../../../../../enum/constants';
import { getIntegrationQrCode } from '../../../../../helpers/utils';
import { NotificationService } from '../../../../../services/notifications.service';
import { ChannelService } from '../../../../../services/channel.service';
import { takeUntil } from 'rxjs';
import { BaseService } from '../../../../../services/base.service';
import { WebSocketService } from '../../../../../services/gateway.service';
import { IntegrationService } from '../../../../../services/integration.service';
import { ISocketIntegrationEvent, SocketIntegrationAction } from '../integration-instagram/integration.interface';
import { BaseIntegration } from '../base-integration/base-integration';
import { phoneValidator } from '../../../../../validators/phone.validator';
import { DialogService } from '../../../../../services/dialog.service';

@Component({
  selector: 'app-integration-viber',
  templateUrl: './integration-viber.component.html',
  styleUrls: ['./integration-viber.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class IntegrationViberComponent extends BaseIntegration implements OnInit, OnDestroy {
  @Input() payload: any;
  @Output() close = new EventEmitter<any>();

  constructor(
    protected override readonly notificationService: NotificationService,
    protected override readonly channelService: ChannelService,
    protected override readonly integrationService: IntegrationService,
    protected readonly baseService: BaseService,
    protected readonly webSocketService: WebSocketService,
    protected readonly dialogService: DialogService,
  ) {
    super(integrationService, notificationService, channelService);
  }

  @ViewChild('qrcode') qrCodeCanvas!: ElementRef;
  protected phoneControl!: FormControl;

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.channel_id = this.payload.channel_id;
    this.phoneControl = new FormControl('', [Validators.required, phoneValidator]);
    // this.phoneControl.setValue('0972045716');
    this.initSockets();
    this.getChannelById();
  }

  private initSockets(): void {
    this.webSocketService
      .viberChannelAction()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.handleChannelAction(data);
        },
      });
  }

  protected createQrCode(): void {
    this.channelService.checkConnect({ type: CHANNEL_TYPE.VIBER }).subscribe({
      next: (data: any) => {
        if (data) {
          if (this.phoneControl.invalid) {
            this.phoneControl.markAsTouched();
            return;
          }

          this.isLoader = true;
          this.integrationService
            .createViber(this.channel_id, this.phoneControl.value, this.isHandleFiles)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              error: (res: any) => {
                (res.error?.errors || []).forEach((err: any) => {
                  this.notificationService.showError('', ERROR_MESSAGES[err.code] || DEFAULT_ERROR);
                  this.isLoader = false;
                });
              },
            });
        }
      },
    });
  }

  private initQRCode(qrUrl?: string) {
    if (!this.qrCodeCanvas?.nativeElement || !qrUrl) return;
    this.qrUrl = qrUrl;

    const qrCode = getIntegrationQrCode(qrUrl, '/assets/svgs/integrations/viber.svg');

    this.qrCodeCanvas.nativeElement.innerHTML = '';
    qrCode.append(this.qrCodeCanvas.nativeElement);
  }

  private handleChannelAction(data: ISocketIntegrationEvent): void {
    if (this.channel_id !== data.channel_id) return;

    switch (data.action) {
      case SocketIntegrationAction.QR_AVAILABLE: {
        this.showQr = true;
        requestAnimationFrame(() => this.initQRCode(data.data?.qrUrl));
        this.isLoader = false;
        break;
      }

      case SocketIntegrationAction.PROCESSED: {
        this.isConnectionProcessed = !data.data?.afterLoginActions;
        this.isLoader = true;
        this.showQr = false;
        break;
      }

      case SocketIntegrationAction.SUCCESS: {
        this.getChannelById();
        this.showQr = false;
        this.isConnectionProcessed = false;
        break;
      }

      case SocketIntegrationAction.FAILED: {
        this.isLoader = false;
        this.showQr = false;
        this.isConnectionProcessed = false;
        if (data.error) {
          this.notificationService.showError(
            activeTranslations.notifications_texts.error.error,
            ERROR_MESSAGES[data.error] || DEFAULT_ERROR,
          );
        }
        break;
      }
    }
  }

  protected get isAdmin(): boolean {
    return !!this.baseService.user?.is_admin;
  }

  protected get vncUrl(): string | null {
    if (!this.isAdmin) return null;
    const number = (this.phoneNumber || '').replace(/\D/g, '');
    if (!number) return null;
    return `http://viber.jestos.org/${number}/vnc.html?password=jikhod-jIbtig-9kofta`;
  }

  protected override deleteChannel(): void {
    this.dialogService
      .openConfirm({ text: 'dialogs.integration-viber-delete' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res) super.deleteChannel();
        },
      });
  }

  protected restartViber(): void {
    this.dialogService
      .openConfirm({ text: 'dialogs.integration-viber-restart' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res) this.sendRestart();
        },
      });
  }

  private sendRestart(): void {
    this.isLoader = true;
    this.integrationService
      .restartViber(this.channel_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isLoader = false;
          this.notificationService.showSuccess(
            '',
            activeTranslations.notifications_texts.success.successfully_updated,
          );
        },
        error: (e: any) => {
          this.isLoader = false;
          (e.error?.errors || []).forEach((err: any) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code] || DEFAULT_ERROR);
          });
        },
      });
  }

  override channelByIdCallback(): void {
    this.phoneControl.setValue(this.phoneNumber);
    if (this.phoneNumber) this.isPhoneInputDisabled = true;
  }

  protected readonly CHANNEL_STATUS = CHANNEL_STATUS;
  protected readonly CHANNEL_STATUS_OBJECT = CHANNEL_STATUS_OBJECT;
  protected readonly PHONES_FOR_INPUT = PHONES_FOR_INPUT;
}
